import AbstractCanvasTransformTool from "ui/canvas/transforms/AbstractCanvasTransformTool";
import {SCENE_FRAMES} from "ui/config";

/**
 * Transform layer: make Swirl effect for each frame
 */
export default class CanvasSwirl extends AbstractCanvasTransformTool {
	/**
	 * @param {number} [twistsFrom]
	 * @param {number} [twistsTo]
	 */
	constructor(twistsFrom, twistsTo) {
		super();

		/**
		 * @type {number}
		 * @private
		 */
		this._twistsFrom = twistsFrom;

		/**
		 * @type {number}
		 * @private
		 */
		this._twistsTo = twistsTo;

		/**
		 * @type {number[]}
		 * @private
		 */
		this._twistsByFrames = [];
	}

	/**
	 * @inheritDoc
	 */
	_isNeedKeyframesForSelection(fromFrame, toFrame) {
		return fromFrame !== toFrame && this._twistsFrom !== this._twistsTo;
	}

	/**
	 * @inheritDoc
	 */
	async _prepare(layer, fromFrame, toFrame) {
		const len = toFrame - fromFrame;
		let twists, twistsStep;

		this._twistsByFrames = new Array(SCENE_FRAMES).fill(0);

		if (len === 0) {
			twistsStep = 0;
			twists = this._twistsFrom;
		} else if (this._twistsFrom === this._twistsTo) {
			twistsStep = 0;
			twists = this._twistsFrom;
		} else {
			twistsStep = (this._twistsTo - this._twistsFrom) / len;
			twists = this._twistsFrom;
		}

		for (let i = fromFrame; i <= toFrame; i++) {
			this._twistsByFrames[i] = twists;
			twists += twistsStep;
		}
	}

	/**
	 * @param {HTMLCanvasElement} canvas
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {HTMLImageElement} img
	 * @param {number} frameIndex
	 * @returns {?T_DataSetItem}
	 * @protected
	 */
	_processImage(canvas, ctx, img, frameIndex) {
		if (this._twistsByFrames[frameIndex] === 0) {
			return null;
		}

		const twists = this._twistsByFrames[frameIndex] / 10;

		/** @type {{x: number, y: number, width: number, height: number}} */
		let selRect;
		if (this._sel && this._sel.hasSelection && this._sel.isRectangularSelection) {
			selRect = this._sel.selectedAreaRect;
		} else {
			return null;
		}

		const swirlX = selRect.x + selRect.width / 2;
		const swirlY = selRect.y + selRect.height / 2;
		const swirlRadius = Math.min(selRect.width, selRect.height) / 2;

		ctx.clearRect(0, 0, this._sceneWidth, this._sceneHeight);
		ctx.save();
		ctx.drawImage(img, 0, 0);

		const w = canvas.width, h = canvas.height;
		const src = ctx.getImageData(0, 0, w, h).data;
		const dst = new Uint8ClampedArray(src);
		let swirlTwists;

		for (let y = selRect.y, toY = selRect.y + selRect.height; y < toY; y++) {
			for (let x = selRect.x, toX = selRect.x + selRect.width; x < toX; x++) {
				const j = (y * w + x) << 2;
				dst[j] = 0;
				dst[j + 1] = 0;
				dst[j + 2] = 0;
				dst[j + 3] = 0;
			}
		}

		for (let y = selRect.y, toY = selRect.y + selRect.height; y < toY; y++) {
			for (let x = selRect.x, toX = selRect.x + selRect.width; x < toX; x++) {
				// compute the distance and angle from the swirl center:
				let pixelX = x - swirlX;
				let pixelY = y - swirlY;
				const pixelDistance = Math.sqrt((pixelX * pixelX) + (pixelY * pixelY));
				swirlTwists = twists * (1 - (pixelDistance / swirlRadius));
				let pixelAngle = Math.atan2(pixelY, pixelX);

				// work out how much of a swirl to apply (1.0 in the center fading out to 0.0 at the radius):
				const swirlAmount = 1 - (pixelDistance / swirlRadius);
				if (swirlAmount > 0.0) {
					const twistAngle = swirlTwists * swirlAmount * Math.PI * 2;

					// adjust the pixel angle and compute the adjusted pixel co-ordinates:
					pixelAngle += twistAngle;
					pixelX = Math.cos(pixelAngle) * pixelDistance;
					pixelY = Math.sin(pixelAngle) * pixelDistance;
				}

				const srcX = Math.round(swirlX + pixelX);
				const srcY = Math.round(swirlY + pixelY);
				const i = (srcY * w + srcX) << 2;
				const j = (y * w + x) << 2;

				dst[j] = src[i];
				dst[j + 1] = src[i + 1];
				dst[j + 2] = src[i + 2];
				dst[j + 3] = src[i + 3];

				const floatX = swirlX + pixelX;
				const floatY = swirlY + pixelY;

				for (let rY = Math.floor(floatY); rY <= Math.ceil(floatY); rY++) {
					for (let rX = Math.floor(floatX); rX <= Math.ceil(floatX); rX++) {
						const percentX = 1 - Math.abs(floatX - rX);
						const percentY = 1 - Math.abs(floatY - rY);
						const percent = percentX * percentY;

						const i = (rY * w + rX) << 2;
						CanvasSwirl.mixColors(i, j, src, dst, percent);
					}
				}
			}
		}

		ctx.putImageData(new ImageData(dst, w), 0, 0);
		ctx.restore();

		return this._dataSet.setData(canvas.toDataURL('image/png'));
	}

	/**
	 * @param {number} i
	 * @param {number} j
	 * @param {Uint8ClampedArray} src
	 * @param {Uint8ClampedArray} dst
	 * @param {number} percent
	 */
	static mixColors(i, j, src, dst, percent) {
		const srcA = (src[i + 3] / 256) * percent;
		const dstA = dst[j + 3] / 256;
		const a = 1 - (1 - srcA) * (1 - dstA); // alpha
		const r = Math.round((src[i + 0] * srcA / a) + (dst[j + 0] * dstA * (1 - srcA) / a));
		const g = Math.round((src[i + 1] * srcA / a) + (dst[j + 1] * dstA * (1 - srcA) / a));
		const b = Math.round((src[i + 2] * srcA / a) + (dst[j + 2] * dstA * (1 - srcA) / a));

		dst[j] = r;
		dst[j + 1] = g;
		dst[j + 2] = b;
		dst[j + 3] = Math.round(a * 255);
	}
}
